import { useCallback, useEffect, useState } from 'react'
import s from './styles.module.scss'
import * as api from 'api'
import { Alert, Layouts, Loader, NoData, Input } from 'components'
import { head, getTimeFormats } from 'helpers'
import { Link, useNavigate } from 'react-router-dom'
import InfiniteScroll from 'components/infiniteScroll'

export default function Contacts() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [pageNo, setPageNo] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [userCalls, setUserCalls] = useState([])

  const getUsers = useCallback(async (page = 1) => {
    if (loading) return
    setLoading(true)

    try {
      const response = await api.admin.user.fetchAll({
        pageNo: page,
        limit: 20,
        searchQuery: searchQuery || undefined
      })

      if (response.code === 200) {
        if (page === 1) {
          setUsers(response.payload.Users || [])
        } else {
          setUsers(prev => [...prev, ...(response.payload.Users || [])])
        }
        setTotalPages(Math.ceil((response.payload.total || 0) / 20))
        setPageNo(page)
      } else {
        Alert.error(response.message || 'Failed to fetch users')
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      Alert.error('Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }, [searchQuery, loading])

  const getUserCalls = useCallback(async (userId) => {
    try {
      const response = await api.admin.call.fetchAll({
        userId,
        perPage: 50,
        pageNo: 1
      })
      if (response.code === 200) {
        setUserCalls(response.payload.calls || [])
      }
    } catch (error) {
      console.error('Error fetching user calls:', error)
    }
  }, [])

  useEffect(() => {
    head({ title: 'Contacts | BCN CRM' })
    getUsers(1)
  }, [searchQuery])

  useEffect(() => {
    if (selectedUser) {
      getUserCalls(selectedUser._id)
    }
  }, [selectedUser, getUserCalls])

  const formatDuration = (seconds) => {
    if (!seconds) return '—'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Contacts'>
        <div className={s.headerActions}>
          <button
            className={s.backButton}
            onClick={() => navigate('/admin/crm')}
          >
            <span className='material-icons-outlined'>arrow_back</span>
            Back to CRM
          </button>
          <Link to='/admin/crm/calls' className={s.actionButton}>
            <span className='material-icons-outlined'>phone</span>
            Manage Calls
          </Link>
        </div>
      </Layouts.Classic>

      <div className={s.content}>
        <div className={s.contactsList}>
          <div className={s.searchSection}>
            <Input.Classic
              placeholder='Search contacts by name, phone, or email...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              iconLeft='search'
            />
          </div>

          <div className={s.contactsGrid}>
            <InfiniteScroll
              next={getUsers}
              filter=''
              currentPage={pageNo}
              hasMore={pageNo < totalPages}
              inLayout
            >
              {users.length === 0 && !loading ? (
                <NoData message='No contacts found' />
              ) : (
                <div className={s.usersGrid}>
                  {users.map(user => (
                    <ContactCard
                      key={user._id}
                      user={user}
                      isSelected={selectedUser?._id === user._id}
                      onClick={() => setSelectedUser(user)}
                    />
                  ))}
                </div>
              )}
            </InfiniteScroll>
            {loading && <Loader color='var(--c-primary)' colorText='var(--c-primary)' />}
          </div>
        </div>

        {selectedUser && (
          <div className={s.contactDetails}>
            <div className={s.contactHeader}>
              <div>
                <h2>{selectedUser.firstName} {selectedUser.lastName}</h2>
                <div className={s.contactInfo}>
                  <div className={s.contactInfoItem}>
                    <span className='material-icons-outlined'>phone</span>
                    <span>{selectedUser.phoneNo}</span>
                  </div>
                  {selectedUser.email && (
                    <div className={s.contactInfoItem}>
                      <span className='material-icons-outlined'>email</span>
                      <span>{selectedUser.email}</span>
                    </div>
                  )}
                  <div className={s.contactInfoItem}>
                    <span className='material-icons-outlined'>location_on</span>
                    <span>{selectedUser.city || 'N/A'}, {selectedUser.state || 'N/A'}</span>
                  </div>
                  {selectedUser.userRefId && (
                    <div className={s.contactInfoItem}>
                      <span className='material-icons-outlined'>badge</span>
                      <span>ID: {selectedUser.userRefId}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                className={s.initiateCallBtn}
                onClick={() => {
                  navigate('/admin/crm/calls', {
                    state: {
                      userId: selectedUser._id,
                      phoneNumber: selectedUser.phoneNo,
                      contactName: `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
                    }
                  })
                }}
              >
                <span className='material-icons-outlined'>phone</span>
                Call Now
              </button>
            </div>

            <div className={s.callHistory}>
              <h3>Call History</h3>
              {userCalls.length === 0 ? (
                <NoData message='No calls recorded for this contact' />
              ) : (
                <div className={s.callsList}>
                  {userCalls.map(call => (
                    <CallHistoryItem key={call._id} call={call} formatDuration={formatDuration} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ContactCard = ({ user, isSelected, onClick }) => {
  return (
    <div
      className={`${s.contactCard} ${isSelected ? s.selected : ''}`}
      onClick={onClick}
    >
      <div className={s.contactCardHeader}>
        <div className={s.contactAvatar}>
          {user.firstName?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className={s.contactCardInfo}>
          <div className={s.contactCardName}>
            {user.firstName} {user.lastName}
          </div>
          <div className={s.contactCardPhone}>{user.phoneNo}</div>
          {user.userRefId && (
            <div className={s.contactCardId}>ID: {user.userRefId}</div>
          )}
        </div>
      </div>
      <div className={s.contactCardDetails}>
        <div className={s.contactCardDetail}>
          <span className='material-icons-outlined'>location_on</span>
          <span>{user.city || 'N/A'}</span>
        </div>
        {user.isApproved && (
          <div className={s.approvedBadge}>Approved</div>
        )}
      </div>
    </div>
  )
}

const CallHistoryItem = ({ call, formatDuration }) => {
  const getStatusColor = (status) => {
    const colors = {
      completed: 'green',
      answered: 'blue',
      missed: 'red',
      busy: 'orange',
      failed: 'red',
      pending: 'gray'
    }
    return colors[status] || 'gray'
  }

  return (
    <div className={s.callHistoryItem}>
      <div className={s.callHistoryHeader}>
        <div className={s.callHistoryInfo}>
          <div className={s.callHistoryDirection}>
            {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
          </div>
          <div className={s.callHistoryTime}>
            {call.startTime ? getTimeFormats(new Date(call.startTime)) : '—'}
          </div>
        </div>
        <div className={`${s.callHistoryStatus} ${s[`status-${getStatusColor(call.status)}`]}`}>
          {call.status}
        </div>
      </div>
      {call.duration > 0 && (
        <div className={s.callHistoryDuration}>
          Duration: {formatDuration(call.duration)}
        </div>
      )}
      {call.notes && (
        <div className={s.callHistoryNotes}>{call.notes}</div>
      )}
      {call.outcome && call.outcome !== 'pending' && (
        <div className={s.callHistoryOutcome}>
          Outcome: <strong>{call.outcome}</strong>
        </div>
      )}
    </div>
  )
}

